import { createAsyncThunk } from '@reduxjs/toolkit';
import focusBearApi from 'services/axios-config';
import {
  updateIsAssigningAdmin,
  updateIsInvitingUser,
  updateAreTeamsLoading,
  updateIsRemovingAdmin,
  updateIsRemovingMember,
  updateIsUpdatingTeamName,
  updateAreMembersLoading,
  updateStaffUsage,
  updateIsStaffUsageLoading,
  updateShowTeamOwnerActionModal,
  updateSelectedTeam,
  updateTeams,
  updateIsAcceptingInvitation,
  updateIsUpdatingExpiryDate,
  updateIsInvitationAccepted,
  updateIsAddingMemberManually,
  updateInvitationLinkHasCustomError,
  updateSelectedMemberKey,
  updateIsAssigningTeacher,
  updateIsRemovingTeacher,
  updateAreJoinCodesLoading,
  updateIsCreatingJoinCode,
  updateJoinCodes,
  updateIsUpdatingMemberRole,
  updateIsUpdatingClassSessionWindow
} from './slice';
import Endpoints from 'constants/endpoints';
import { HTTP_STATS_CODE } from 'constants/general';
import { toast } from 'react-toastify';
import { t } from 'services/i18n.service';
import { RootState } from 'store';
import {
  BulkDeleteMembersDto,
  CreateBatchJoinCodesPayload,
  CreateJoinCodePayload,
  JoinCode,
  StaffUsageResponse,
  Team,
  UpdateClassSessionWindowPayload,
  UpdatePendingMemberRolePayload
} from 'interfaces/team.interface';
import { AxiosError } from 'axios';
import { TEAM_MEMBER_ROLE } from 'enums';
import { InviteMemberPayload } from 'interfaces/subscription.interface';
import { captureException } from 'services/posthog.service';
import { clearStoredJoinCode } from 'utils/team-join.util';
import {
  fetchClassMembers,
  fetchTeacherClasses
} from 'store/reducer/class-management/extra';

export const fetchAdminTeams = createAsyncThunk(
  'team/fetch_admin_teams',
  async (_, { dispatch }) => {
    try {
      dispatch(updateAreTeamsLoading(true));
      // include_teaching also returns teams the user only teaches, each row
      // carrying access_role ('admin' | 'teacher') — Part H nav + teacher view.
      const { data } = await focusBearApi.get(
        `${Endpoints.TEAM_MANAGEMENT}/admin-teams`,
        { params: { include_teaching: true } }
      );
      return data ?? [];
    } catch (error) {
      dispatch(updateAreTeamsLoading(false));
      captureException(JSON.stringify(error));
    }
  }
);

export const fetchAllMembers = createAsyncThunk(
  'team/fetch_all_members',
  async (team_id: string, { dispatch }) => {
    try {
      dispatch(updateAreMembersLoading(true));
      const { data } = await focusBearApi.get(
        `${Endpoints.TEAM_MANAGEMENT}/all-members`,
        {
          params: {
            team_id
          }
        }
      );
      return data;
    } catch (error) {
      dispatch(updateAreMembersLoading(false));
      captureException(JSON.stringify(error));
    }
  }
);

export const fetchStaffUsage = createAsyncThunk(
  'team/fetch_staff_usage',
  async (teamId: string, { dispatch }) => {
    try {
      dispatch(updateIsStaffUsageLoading(true));
      const { data, status } = await focusBearApi.get<StaffUsageResponse>(
        Endpoints.SCHOOL_STAFF_USAGE(teamId),
        {
          // A non-school-admin (e.g. a teacher) gets 403. Resolving it here keeps the
          // global interceptor from redirecting them to the Forbidden page.
          validateStatus: (responseStatus) =>
            responseStatus === HTTP_STATS_CODE.SUCCESS ||
            responseStatus === HTTP_STATS_CODE.FORBIDDEN
        }
      );
      dispatch(
        updateStaffUsage(status === HTTP_STATS_CODE.SUCCESS ? data : null)
      );
    } catch (error) {
      dispatch(updateStaffUsage(null));
      captureException(JSON.stringify(error));
    } finally {
      dispatch(updateIsStaffUsageLoading(false));
    }
  }
);

export const inviteMember = createAsyncThunk(
  'team/invite_member',
  async (payload: InviteMemberPayload, { dispatch }) => {
    try {
      dispatch(updateIsInvitingUser(true));
      const { status } = await focusBearApi.post(
        `${Endpoints.TEAM_MANAGEMENT}/invite-member`,
        payload
      );
      if (status === HTTP_STATS_CODE.CREATED) {
        dispatch(updateShowTeamOwnerActionModal(false));
        dispatch(fetchAllMembers(payload.team_id));
        toast.success(t('admin_team.invitation_sent_successfully'));
      } else {
        toast.error(t('admin_team.could_not_send_the_invitation'));
      }
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
    } finally {
      dispatch(updateIsInvitingUser(false));
    }
  }
);

export const removeMember = createAsyncThunk(
  'team/remove_member',
  async (
    data: { email?: string; member_id?: string; team_id: string },
    { dispatch }
  ) => {
    try {
      dispatch(updateIsRemovingMember(true));
      await focusBearApi.post(
        `${Endpoints.TEAM_MANAGEMENT}/remove-member`,
        data
      );
      dispatch(updateIsRemovingMember(false));
    } catch (error) {
      dispatch(updateIsRemovingMember(false));
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
    }
  }
);

/**
 * Bulk remove members — for team OWNERS (DELETE /team/bulk-delete-members).
 * Sends a single request with all selected member IDs / emails, eliminating
 * per-member API calls that caused 429 rate-limit errors.
 */
export const bulkDeleteMembers = createAsyncThunk(
  'team/bulk_delete_members',
  async (payload: BulkDeleteMembersDto, { dispatch }) => {
    try {
      dispatch(updateIsRemovingMember(true));
      await focusBearApi.delete(Endpoints.TEAM_BULK_DELETE_MEMBERS, {
        data: payload
      });
    } catch (error) {
      captureException(JSON.stringify(error));
      throw error;
    } finally {
      dispatch(updateIsRemovingMember(false));
    }
  }
);

/**
 * Bulk remove members — for team ADMINS (POST /team/bulk-remove-members).
 * Sends a single request with all selected member IDs / emails, eliminating
 * per-member API calls that caused 429 rate-limit errors.
 */
export const bulkRemoveMembers = createAsyncThunk(
  'team/bulk_remove_members',
  async (payload: BulkDeleteMembersDto, { dispatch }) => {
    try {
      dispatch(updateIsRemovingMember(true));
      await focusBearApi.post(Endpoints.TEAM_BULK_REMOVE_MEMBERS, payload);
    } catch (error) {
      captureException(JSON.stringify(error));
      throw error;
    } finally {
      dispatch(updateIsRemovingMember(false));
    }
  }
);

export const assignAdmin = createAsyncThunk(
  'team/assign_admin',
  async (
    data: { email?: string; member_id?: string; team_id: string },
    { dispatch, rejectWithValue }
  ) => {
    try {
      dispatch(updateIsAssigningAdmin(true));
      const { status } = await focusBearApi.post(
        `${Endpoints.TEAM_MANAGEMENT}/assign-admin`,
        data
      );
      if (status !== HTTP_STATS_CODE.CREATED) {
        toast.error(t('admin_team.could_not_assign_admin_role'));
        return rejectWithValue(null);
      }
      dispatch(fetchAllMembers(data.team_id));
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
      return rejectWithValue(error);
    } finally {
      dispatch(updateIsAssigningAdmin(false));
      dispatch(updateIsRemovingAdmin(false));
    }
  }
);

export const removeAdmin = createAsyncThunk(
  'team/remove_admin',
  async (
    data: { email?: string; member_id?: string; team_id: string },
    { dispatch, rejectWithValue }
  ) => {
    try {
      dispatch(updateIsRemovingAdmin(true));
      const { status } = await focusBearApi.post(
        `${Endpoints.TEAM_MANAGEMENT}/remove-admin`,
        data
      );
      if (status !== HTTP_STATS_CODE.CREATED) {
        toast.error(t('admin_team.could_not_remove_admin_role'));
        return rejectWithValue(null);
      }
      dispatch(fetchAllMembers(data.team_id));
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
      return rejectWithValue(error);
    } finally {
      dispatch(updateIsRemovingAdmin(false));
      dispatch(updateSelectedMemberKey());
    }
  }
);

export const assignTeacher = createAsyncThunk(
  'team/assign_teacher',
  async (
    data: { member_id: string; team_id: string },
    { dispatch, rejectWithValue }
  ) => {
    try {
      dispatch(updateIsAssigningTeacher(true));
      const { status } = await focusBearApi.post(
        `${Endpoints.TEAM_MANAGEMENT}/assign-teacher`,
        data
      );
      if (status !== HTTP_STATS_CODE.CREATED) {
        toast.error(t('school.could_not_assign_teacher_role'));
        return rejectWithValue(null);
      }
      dispatch(fetchAllMembers(data.team_id));
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(error);
      return rejectWithValue(error);
    } finally {
      dispatch(updateIsAssigningTeacher(false));
      dispatch(updateSelectedMemberKey());
    }
  }
);

export const removeTeacher = createAsyncThunk(
  'team/remove_teacher',
  async (
    data: { member_id: string; team_id: string },
    { dispatch, rejectWithValue }
  ) => {
    try {
      dispatch(updateIsRemovingTeacher(true));
      const { status } = await focusBearApi.post(
        `${Endpoints.TEAM_MANAGEMENT}/remove-teacher`,
        data
      );
      if (status !== HTTP_STATS_CODE.CREATED) {
        toast.error(t('school.could_not_remove_teacher_role'));
        return rejectWithValue(null);
      }
      dispatch(fetchAllMembers(data.team_id));
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(error);
      return rejectWithValue(error);
    } finally {
      dispatch(updateIsRemovingTeacher(false));
      dispatch(updateSelectedMemberKey());
    }
  }
);

/**
 * Atomic school/team role switch (PUT /team-management/member-role).
 * Replaces chaining assign/remove admin/teacher for MemberRoleSelect.
 */
export const updateMemberRole = createAsyncThunk(
  'team/update_member_role',
  async (
    data: { team_id: string; member_id: string; role: TEAM_MEMBER_ROLE },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      dispatch(updateIsUpdatingMemberRole(true));
      const { status } = await focusBearApi.put(
        `${Endpoints.TEAM_MANAGEMENT}/member-role`,
        data
      );
      if (
        ![HTTP_STATS_CODE.SUCCESS, HTTP_STATS_CODE.CREATED].includes(status)
      ) {
        toast.error(t('admin_team.could_not_update_member_role'));
        return rejectWithValue(null);
      }
      dispatch(fetchAllMembers(data.team_id));
      dispatch(fetchAdminTeams());
      dispatch(fetchTeacherClasses());
      const selectedClass = (getState() as RootState).classManagement
        .selectedClass;
      if (selectedClass?.id) {
        dispatch(fetchClassMembers(selectedClass.id));
      }
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(error);
      return rejectWithValue(error);
    } finally {
      dispatch(updateIsUpdatingMemberRole(false));
      dispatch(updateSelectedMemberKey());
    }
  }
);

export const modifyTeamName = createAsyncThunk(
  'team/modify_team_name',
  async (data: { name: string; team_id: string }, { dispatch, getState }) => {
    try {
      dispatch(updateIsUpdatingTeamName(true));
      const { status } = await focusBearApi.put(
        `${Endpoints.TEAM_MANAGEMENT}/name`,
        data
      );
      dispatch(updateIsUpdatingTeamName(false));
      if (status === HTTP_STATS_CODE.SUCCESS) {
        const { teams, selectedTeam } = (getState() as RootState).team;
        dispatch(
          updateTeams(
            teams.map((team: Team) =>
              team.id === data.team_id ? { ...team, name: data.name } : team
            )
          )
        );
        if (selectedTeam) {
          dispatch(updateSelectedTeam({ ...selectedTeam, name: data.name }));
        }
        return true;
      }
      toast.error(t('admin_team.could_not_update_team_name'));
      return false;
    } catch (error) {
      dispatch(updateIsUpdatingTeamName(false));
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
      return false;
    }
  }
);

export const updateClassSessionWindow = createAsyncThunk(
  'team/update_class_session_window',
  async (
    payload: UpdateClassSessionWindowPayload,
    { dispatch, getState }
  ) => {
    try {
      dispatch(updateIsUpdatingClassSessionWindow(true));
      await focusBearApi.put(
        Endpoints.TEAM_CLASS_SESSION_WINDOW,
        payload
      );
      const { teams, selectedTeam } = (getState() as RootState).team;
      const windowValues = {
        class_session_window_start: payload.window_start,
        class_session_window_end: payload.window_end
      };
      dispatch(
        updateTeams(
          teams.map((team: Team) =>
            team.id === payload.team_id ? { ...team, ...windowValues } : team
          )
        )
      );
      selectedTeam &&
        dispatch(updateSelectedTeam({ ...selectedTeam, ...windowValues }));
      toast.success(t('school.session_window_updated'));
      return true;
    } catch (error) {
      const axiosError = error as AxiosError<string | { message?: string }>;
      const responseData = axiosError.response?.data;
      const serverMessage =
        typeof responseData === 'string'
          ? responseData
          : responseData?.message;
      toast.error(serverMessage || t('could_not_process_the_request'));
      captureException(error);
      return false;
    } finally {
      dispatch(updateIsUpdatingClassSessionWindow(false));
    }
  }
);

export const acceptInvitation = createAsyncThunk(
  'team/accept_invitation',
  async (token: string, { dispatch }) => {
    try {
      dispatch(updateIsAcceptingInvitation(true));
      const { status } = await focusBearApi.post(
        `${Endpoints.TEAM_MANAGEMENT}/accept-invitation`,
        { token }
      );
      if (status === HTTP_STATS_CODE.CREATED) {
        toast.success(t('admin_team.invitation_accepted_successfully'));
        dispatch(updateIsInvitationAccepted(true));
      } else {
        toast.error(t('could_not_process_the_request'));
      }
    } catch (error) {
      const errorData = error as AxiosError;
      const errorMessage = errorData?.response?.data;
      if (typeof errorMessage === 'string') {
        dispatch(
          updateInvitationLinkHasCustomError({
            hasError: true,
            message: errorMessage
          })
        );
      } else {
        toast.error(t('could_not_process_the_request'));
      }
      captureException(JSON.stringify(error));
    } finally {
      dispatch(updateIsAcceptingInvitation(false));
    }
  }
);

export const updateMemberExpiryDate = createAsyncThunk(
  'team/update_member_expiry_data',
  async (
    data: {
      member_id?: string;
      email?: string;
      team_id: string;
      expiry_date: string;
    },
    { dispatch }
  ) => {
    try {
      dispatch(updateIsUpdatingExpiryDate(true));
      const { status } = await focusBearApi.put(
        `${Endpoints.TEAM_MANAGEMENT}/member-expiry`,
        data
      );

      if (status === HTTP_STATS_CODE.SUCCESS) {
        dispatch(fetchAllMembers(data.team_id));
      } else {
        toast.error(t('admin_team.could_not_remove_admin_role'));
      }
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
    } finally {
      dispatch(updateIsUpdatingExpiryDate(false));
      dispatch(updateSelectedMemberKey());
    }
  }
);

export const addMemberManually = createAsyncThunk(
  'team/add-member-manually',
  async (
    data: { user_id: string; team_id: string; role?: TEAM_MEMBER_ROLE },
    { dispatch, getState }
  ) => {
    try {
      dispatch(updateIsAddingMemberManually(true));
      const { status } = await focusBearApi.post(
        `${Endpoints.TEAM_MANAGEMENT}/add-member-manually`,
        data
      );
      dispatch(updateIsAddingMemberManually(false));
      if (
        [HTTP_STATS_CODE.CREATED, HTTP_STATS_CODE.NO_CONTENT].includes(status)
      ) {
        const selectedTeam = (getState() as RootState).team.selectedTeam;
        selectedTeam && dispatch(fetchAllMembers(selectedTeam?.id));
        dispatch(updateShowTeamOwnerActionModal(false));
      } else {
        toast.error(t('could_not_process_the_request'));
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      const statusCode = axiosError.response?.status;
      if (axiosError.response?.status === HTTP_STATS_CODE.NOT_FOUND) {
        toast.error(
          t('the_user_with_id_doesnt_exists', { user_id: data.user_id })
        );
      } else if (statusCode === HTTP_STATS_CODE.BAD_REQUEST) {
        axiosError.config?.data
          ? toast.error(axiosError.config.data)
          : toast.error(t('could_not_process_the_request'));
      } else {
        toast.error(t('could_not_process_the_request'));
      }
      dispatch(updateIsAddingMemberManually(false));
      captureException(JSON.stringify(error));
    }
  }
);

export const joinTeamViaJoinCode = createAsyncThunk(
  'team/join_team_via_join_code',
  async (joinCode: string) => {
    try {
      const { status, data } = await focusBearApi.post(
        `${Endpoints.TEAM_MANAGEMENT}/join`,
        { join_code: joinCode }
      );

      if (status === HTTP_STATS_CODE.CREATED) {
        clearStoredJoinCode();
        toast.success(t('admin_team.successfully_joined_team'));
        return true;
      }

      if (status === HTTP_STATS_CODE.SUCCESS) {
        clearStoredJoinCode();
        toast.info(t('admin_team.already_a_member'));
        return true;
      }

      return data?.message ? false : false;
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const statusCode = axiosError.response?.status;
      const serverMessage = axiosError.response?.data?.message;

      clearStoredJoinCode();

      if (statusCode === HTTP_STATS_CODE.NOT_FOUND) {
        toast.error(t('admin_team.invalid_join_code'));
      } else if (statusCode === HTTP_STATS_CODE.BAD_REQUEST && serverMessage) {
        toast.error(serverMessage);
      } else {
        toast.error(t('could_not_process_the_request'));
      }

      captureException(JSON.stringify(error));
      return false;
    }
  }
);

export const fetchJoinCodes = createAsyncThunk(
  'team/fetch_join_codes',
  async (teamId: string, { dispatch }) => {
    try {
      dispatch(updateAreJoinCodesLoading(true));
      const { data } = await focusBearApi.get(Endpoints.TEAM_JOIN_CODES, {
        params: { team_id: teamId }
      });
      dispatch(updateJoinCodes(data ?? []));
    } catch (error) {
      captureException(JSON.stringify(error));
    } finally {
      dispatch(updateAreJoinCodesLoading(false));
    }
  }
);

export const createJoinCode = createAsyncThunk(
  'team/create_join_code',
  async (payload: CreateJoinCodePayload, { dispatch }) => {
    try {
      dispatch(updateIsCreatingJoinCode(true));
      const { status, data } = await focusBearApi.post(
        Endpoints.TEAM_JOIN_CODES,
        payload
      );
      if (status === HTTP_STATS_CODE.CREATED) {
        toast.success(t('admin_team.code_created'));
        dispatch(fetchJoinCodes(payload.team_id));
        return data as JoinCode;
      }
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
    } finally {
      dispatch(updateIsCreatingJoinCode(false));
    }
  }
);

export const createBatchJoinCodes = createAsyncThunk(
  'team/create_batch_join_codes',
  async (payload: CreateBatchJoinCodesPayload, { dispatch }) => {
    try {
      dispatch(updateIsCreatingJoinCode(true));
      const { status } = await focusBearApi.post(
        Endpoints.TEAM_JOIN_CODES_BATCH,
        payload
      );
      if (status === HTTP_STATS_CODE.CREATED) {
        toast.success(
          t('admin_team.codes_created', { count: payload.count })
        );
        dispatch(fetchJoinCodes(payload.team_id));
      }
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
    } finally {
      dispatch(updateIsCreatingJoinCode(false));
    }
  }
);

export const deactivateJoinCode = createAsyncThunk(
  'team/deactivate_join_code',
  async (
    { codeId, teamId }: { codeId: string; teamId: string },
    { dispatch }
  ) => {
    try {
      await focusBearApi.delete(Endpoints.TEAM_JOIN_CODES, {
        params: { code_id: codeId }
      });
      toast.success(t('admin_team.code_deactivated'));
      dispatch(fetchJoinCodes(teamId));
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
    }
  }
);

export const updatePendingMemberRole = createAsyncThunk(
  'team/update_pending_member_role',
  async (data: UpdatePendingMemberRolePayload, { dispatch, rejectWithValue }) => {
    dispatch(updateIsUpdatingMemberRole(true));
    try {
      await focusBearApi.put(Endpoints.TEAM_PENDING_MEMBER_ROLE, data);
      await dispatch(fetchAllMembers(data.team_id)).unwrap();
    } catch (error) {
      dispatch(fetchAllMembers(data.team_id));
      toast.error(t('admin_team.could_not_update_member_role'));
      captureException(error);
      return rejectWithValue(null);
    } finally {
      dispatch(updateIsUpdatingMemberRole(false));
      dispatch(updateSelectedMemberKey());
    }
  }
);
